class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);


/* ============================================================
   VF / Pebble & Wick — Cinematic Cart Notification Enhancements
   Safe layer on top of Dawn cart-notification.js.
   Keeps add-to-cart notification rendering, focus trap, cart:view event and cart bubble updates intact.
   ============================================================ */

(() => {
  const CartNotificationElement = customElements.get('cart-notification');
  if (!CartNotificationElement || CartNotificationElement.prototype.__vfCinematicPatched) return;

  CartNotificationElement.prototype.__vfCinematicPatched = true;

  const originalOpen = CartNotificationElement.prototype.open;
  const originalClose = CartNotificationElement.prototype.close;
  const originalRenderContents = CartNotificationElement.prototype.renderContents;
  const originalDispatchCartViewEvent = CartNotificationElement.prototype.dispatchCartViewEvent;
  const originalHandleBodyClick = CartNotificationElement.prototype.handleBodyClick;
  const originalSetActiveElement = CartNotificationElement.prototype.setActiveElement;

  const root = document.documentElement;

  const setNotificationState = (cartNotification, state, detail = {}) => {
    const notification = cartNotification.notification || document.getElementById('cart-notification');

    cartNotification.dataset.vfCartNotificationState = state;
    notification?.setAttribute('data-vf-cart-notification-state', state);

    cartNotification.classList.toggle('vf-cart-notification-open', state === 'open');
    cartNotification.classList.toggle('vf-cart-notification-closed', state === 'closed');
    cartNotification.classList.toggle('vf-cart-notification-rendering', state === 'rendering');
    cartNotification.classList.toggle('vf-cart-notification-closing', state === 'closing');
    cartNotification.classList.toggle('vf-cart-notification-view-event', state === 'view-event');

    notification?.classList.toggle('vf-cart-notification-open', state === 'open');
    notification?.classList.toggle('vf-cart-notification-rendering', state === 'rendering');
    notification?.classList.toggle('vf-cart-notification-closing', state === 'closing');

    root.classList.toggle('vf-cart-notification-is-open', state === 'open');
    document.body?.classList.toggle('vf-cart-notification-is-open', state === 'open');

    cartNotification.dispatchEvent(
      new CustomEvent(`vf:cart-notification-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const enhanceNotification = (cartNotification) => {
    if (!cartNotification || cartNotification.__vfCinematicEnhanced) return;
    cartNotification.__vfCinematicEnhanced = true;

    cartNotification.classList.add('vf-cart-notification-enhanced');

    const notification = cartNotification.notification || document.getElementById('cart-notification');
    notification?.classList.add('vf-cart-notification-shell');

    cartNotification.querySelectorAll('button[type="button"]').forEach((button) => {
      button.classList.add('vf-cart-notification__close');
    });

    cartNotification.querySelectorAll('a, button').forEach((element, index) => {
      element.style.setProperty('--vf-cart-notification-action-index', index);
    });

    setNotificationState(cartNotification, notification?.classList.contains('active') ? 'open' : 'closed', {
      initial: true
    });
  };

  const markRenderedContent = (cartNotification) => {
    cartNotification.querySelector('#cart-notification-product')?.classList.add('vf-cart-notification__product');
    cartNotification.querySelector('#cart-notification-button')?.classList.add('vf-cart-notification__actions');

    cartNotification.querySelectorAll('.cart-notification-product, .cart-notification-product__image').forEach((element) => {
      element.classList.add('vf-cart-notification__rendered-content');
    });

    cartNotification.querySelectorAll('a, button').forEach((element, index) => {
      element.style.setProperty('--vf-cart-notification-action-index', index);
    });
  };

  const pulseCartIcon = () => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    bubble.classList.remove('vf-cart-notification-bubble-pulse');
    void bubble.offsetWidth;
    bubble.classList.add('vf-cart-notification-bubble-pulse');

    window.setTimeout(() => {
      bubble.classList.remove('vf-cart-notification-bubble-pulse');
    }, 820);
  };

  CartNotificationElement.prototype.open = function vfOpen() {
    enhanceNotification(this);
    setNotificationState(this, 'opening', {
      activeElement: this.activeElement,
      cartItemKey: this.cartItemKey
    });

    const result = originalOpen.call(this);

    window.requestAnimationFrame(() => {
      markRenderedContent(this);
      setNotificationState(this, 'open', {
        activeElement: this.activeElement,
        cartItemKey: this.cartItemKey
      });

      pulseCartIcon();
    });

    return result;
  };

  CartNotificationElement.prototype.close = function vfClose() {
    setNotificationState(this, 'closing', {
      activeElement: this.activeElement,
      cartItemKey: this.cartItemKey
    });

    const result = originalClose.call(this);

    window.setTimeout(() => {
      setNotificationState(this, 'closed', {
        activeElement: this.activeElement,
        cartItemKey: this.cartItemKey
      });
    }, 260);

    return result;
  };

  CartNotificationElement.prototype.renderContents = function vfRenderContents(parsedState) {
    enhanceNotification(this);
    setNotificationState(this, 'rendering', {
      parsedState,
      key: parsedState?.key
    });

    const result = originalRenderContents.call(this, parsedState);

    markRenderedContent(this);

    this.dispatchEvent(
      new CustomEvent('vf:cart-notification-rendered', {
        bubbles: true,
        detail: {
          parsedState,
          key: parsedState?.key,
          cartItemKey: this.cartItemKey
        }
      })
    );

    return result;
  };

  CartNotificationElement.prototype.dispatchCartViewEvent = async function vfDispatchCartViewEvent() {
    setNotificationState(this, 'view-event', {
      cartItemKey: this.cartItemKey
    });

    const result = await originalDispatchCartViewEvent.call(this);

    this.dispatchEvent(
      new CustomEvent('vf:cart-notification-view-event-dispatched', {
        bubbles: true,
        detail: {
          cartItemKey: this.cartItemKey
        }
      })
    );

    if (this.notification?.classList.contains('active')) {
      setNotificationState(this, 'open', {
        cartItemKey: this.cartItemKey
      });
    }

    return result;
  };

  CartNotificationElement.prototype.handleBodyClick = function vfHandleBodyClick(event) {
    const target = event.target;
    const shouldClose = target !== this.notification && !target.closest('cart-notification');

    if (shouldClose) {
      this.classList.add('vf-cart-notification-click-outside');
    }

    const result = originalHandleBodyClick.call(this, event);

    if (shouldClose) {
      window.setTimeout(() => {
        this.classList.remove('vf-cart-notification-click-outside');
      }, 360);
    }

    return result;
  };

  CartNotificationElement.prototype.setActiveElement = function vfSetActiveElement(element) {
    const result = originalSetActiveElement.call(this, element);

    this.dispatchEvent(
      new CustomEvent('vf:cart-notification-active-element-set', {
        bubbles: true,
        detail: {
          activeElement: element
        }
      })
    );

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('cart-notification').forEach(enhanceNotification);
  }, { once: true });

  document.addEventListener('keyup', (event) => {
    if (event.code !== 'Escape') return;

    document.querySelectorAll('cart-notification').forEach((cartNotification) => {
      if (!cartNotification.notification?.classList.contains('active')) return;

      cartNotification.classList.add('vf-cart-notification-escape');

      window.setTimeout(() => {
        cartNotification.classList.remove('vf-cart-notification-escape');
      }, 360);
    });
  });

  document.addEventListener('click', (event) => {
    const action = event.target.closest?.('cart-notification a, cart-notification button');
    if (!action) return;

    action.classList.add('vf-cart-notification-action-clicked');

    window.setTimeout(() => {
      action.classList.remove('vf-cart-notification-action-clicked');
    }, 420);
  });
})();
